import { supabase } from '@/core/database/supabase';
import type { WorkOrderNewFormValues, NewReferenceData } from '../types/workOrderNew.types';

export const workOrderNewService = {
  async getReferenceData(companyId: string): Promise<NewReferenceData> {
    const [
      projectsResult,
      zonesResult,
      phasesResult,
      boqItemsResult,
      teamsResult,
      usersResult,
      categoriesResult,
    ] = await Promise.all([
      supabase
        .from('projects')
        .select('id, code, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true }),
      supabase
        .from('project_zones')
        .select('id, project_id, zone_code, zone_name')
        .eq('company_id', companyId)
        .order('zone_name', { ascending: true }),
      supabase
        .from('project_phases')
        .select('id, project_id, code, name')
        .eq('company_id', companyId)
        .order('display_order', { ascending: true }),
      supabase
        .from('construction_boq_items')
        .select('id, project_id, zone_id, phase_id, item_code, item_name, unit, quantity')
        .eq('company_id', companyId)
        .order('item_code', { ascending: true })
        .limit(500),
      supabase
        .from('teams')
        .select('id, project_id, code, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true }),
      supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('full_name', { ascending: true }),
      supabase
        .from('categories')
        .select('id, code, name, parent_id, level, path, group:category_groups!inner(code)')
        .or(companyId ? `company_id.eq.${companyId},company_id.is.null` : 'company_id.is.null')
        .eq('is_active', true)
        .eq('group.code', 'work')
        .order('display_order', { ascending: true }),
    ]);

    const error = [
      projectsResult.error,
      zonesResult.error,
      phasesResult.error,
      boqItemsResult.error,
      teamsResult.error,
      usersResult.error,
      categoriesResult.error,
    ].find(Boolean);
    if (error) throw error;
 
    return {
      projects: (projectsResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
      })),
      zones: (zonesResult.data || []).map((item) => ({
        id: item.id,
        label: [item.zone_code, item.zone_name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
      phases: (phasesResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
      boqItems: (boqItemsResult.data || []).map((item) => ({
        id: item.id,
        label: [item.item_code, item.item_name].filter(Boolean).join(' - '),
        project_id: item.project_id,
        zone_id: item.zone_id,
        phase_id: item.phase_id,
        unit: item.unit,
        quantity: item.quantity,
      })),
      teams: (teamsResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
      users: (usersResult.data || []).map((item) => ({
        id: item.id,
        label: item.full_name || item.email || item.id,
      })),
      categories: (categoriesResult.data || []).map((item: any) => ({
        id: item.id,
        label: item.name,
        code: item.code,
        parent_id: item.parent_id,
        level: item.level,
        path: item.path,
      })),
    };
  },

  async createWorkOrder(companyId: string, userId: string, values: WorkOrderNewFormValues) {
    const description = values.description.trim();
    const plannedStartAt = combineDateTime(values.planned_start_date, values.planned_start_time);
    const plannedEndAt = combineDateTime(values.planned_end_date, values.planned_end_time);
    const workOrderCode = values.work_order_code.trim() || generateWorkOrderCode();

    const payload: any = {
      company_id: companyId,
      project_id: values.project_id,
      zone_id: values.zone_id || null,
      phase_id: values.phase_id || null,
      work_order_code: workOrderCode,
      title: values.title.trim(),
      description: description || null,
      work_type: values.work_type || 'construction',
      priority_code: values.priority_code || 'medium',
      status_code: values.assigned_team_id || values.lead_user_id ? 'assigned' : 'draft',
      qa_status_code: 'pending',
      lead_user_id: values.lead_user_id || null,
      assigned_by: userId,
      notes: values.notes.trim() || null,
      category_id: values.category_id || null,
      sub_category_id: values.sub_category_id || null,
      created_by: userId,
      due_date: values.planned_end_date || null,
      estimated_hours: parseNumber(values.estimated_hours),
      
      // Construction fields
      boq_item_id: values.boq_item_id || null,
      assigned_team_id: values.assigned_team_id || null,
      planned_start_at: plannedStartAt,
      planned_end_at: plannedEndAt,
      planned_quantity: parseNumber(values.planned_quantity),
      unit: values.unit.trim() || 'lot',
      actual_hours: 0,
      actual_quantity: 0,
      weather_blocked: false,
      source_type: 'manual',

      // Maintenance fields (now in same table)
      area_m2: parseNumber(values.area_m2),
      tree_count: parseNumber(values.tree_count),
      lawn_area_m2: parseNumber(values.lawn_area_m2),
      shrub_area_m2: parseNumber(values.shrub_area_m2),
      hedge_length_m: parseNumber(values.hedge_length_m),
      material_quantity: parseNumber(values.material_quantity),
      scheduled_date: values.planned_start_date || null,
      team_id: values.assigned_team_id || null, // Keeping for compatibility if needed
    };

    const { data, error } = await supabase
      .from('work_orders')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;

    // 2. Insert Collaborators into unified work_order_members
    if (values.collaborator_ids && values.collaborator_ids.length > 0) {
      const memberPayloads = values.collaborator_ids.map(uid => ({
        work_order_id: data.id,
        user_id: uid,
        role_code: 'member',
        company_id: companyId,
        project_id: values.project_id
      }));

      const { error: memberError } = await supabase
        .from('work_order_members')
        .insert(memberPayloads);

      if (memberError) {
        console.error('[workOrderNewService] Failed to insert collaborators:', memberError);
      }
    }

    // Record Audit Log
    try {
      const { auditService } = await import('@/services/audit.service').then(m => ({ auditService: m.default }));
      await auditService.log({
        project_id: data.project_id,
        company_id: data.company_id,
        action_code: 'create',
        module_code: 'work_orders',
        entity_type: 'work_orders',
        entity_id: data.id,
        summary: `Tạo mới lệnh thi công: ${data.work_order_code} - ${data.title}`,
        after_data: data as any
      });
    } catch (logErr) {
      console.error('[workOrderNewService] Failed to record audit log:', logErr);
    }

    return data;
  }
};

function parseNumber(value: string): number {
  const parsed = Number(String(value || '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
}

function combineDateTime(date: string, time: string): string | null {
  if (!date) return null;
  const localDate = new Date(`${date}T${time || '00:00'}:00`);
  return Number.isNaN(localDate.getTime()) ? null : localDate.toISOString();
}

function generateWorkOrderCode(): string {
  const stamp = new Date();
  const datePart = stamp.toISOString().slice(0, 10).replace(/-/g, '');
  const timePart = String(stamp.getTime()).slice(-5);
  return `WO-${datePart}-${timePart}`;
}
